// A line typed as "1." or "- " is a list marker to Markdown. The library that saves the editor escapes a leading `#`
// and `>` but not list markers (a gap its own source notes), so a typed "1." was saved as list syntax and came back - in the
// editor, on the portal, in Teams - as a numbered list item, each one restarting at 1. Typed inside a list item it
// was worse: `1. 1.` is an item holding a second, empty list, which reopened as two levels and lost its numbers.

interface Range {
  offset: number;
  length: number;
}

interface RawBlock {
  type: string;
  text: string;
  inlineStyleRanges: Range[];
  entityRanges: Range[];
}

/** Lines whose text Markdown would read as the start of a list: plain lines, list items and quotes. */
const TEXT_LINES = new Set(['unstyled', 'ordered-list-item', 'unordered-list-item', 'blockquote']);

const ORDERED = /^( {0,3})(\d{1,9})([.)])(?=\s|$)/;
const BULLET = /^( {0,3})[-+*](?=\s|$)/;

/** Where the backslash goes: before an ordered marker's `.` or `)`, or before a bullet. -1 when the line is not one. */
const escapeAt = (text: string): number => {
  const ordered = ORDERED.exec(text);
  if (ordered) return ordered[1].length + ordered[2].length;
  const bullet = BULLET.exec(text);
  return bullet ? bullet[1].length : -1;
};

/**
 * Before saving: text that would read as the start of a list gets a backslash, as the library does for `#` and `>`,
 * so it stays the text that was typed everywhere it is shown - on a plain line, and inside a list item or a quote,
 * where the library writes its own marker first. Anything not at the start of the text is left alone. Styles and
 * links after the backslash move along by one.
 */
export const escapeTypedListMarkers = <T extends RawBlock>(blocks: T[]): T[] =>
  blocks.map(block => {
    if (!TEXT_LINES.has(block.type)) return block;
    const at = escapeAt(block.text);
    if (at < 0) return block;
    const shift = (range: Range): Range => {
      if (range.offset >= at) return { ...range, offset: range.offset + 1 };
      if (range.offset + range.length > at) return { ...range, length: range.length + 1 };
      return range;
    };
    return {
      ...block,
      text: `${block.text.slice(0, at)}\\${block.text.slice(at)}`,
      inlineStyleRanges: block.inlineStyleRanges.map(shift),
      entityRanges: block.entityRanges.map(shift)
    };
  });
